import { css } from '@emotion/react'
import { Checkbox, HFlow, Modal, ModalFooter, ModalFooterButton, ModalHeader, Theme, useTheme } from 'bold-ui'
import { confirm } from 'components/modals/confirm'
import { addDays, startOfDay } from 'date-fns'
import { BundleFhirComAssinaturaPendente, LembreteProfissional } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { ChangeEvent, useState } from 'react'

import { AssinadorRepmModalBody } from './AssinadorRepmModalBody'

export interface AssinadorRepmModalProps {
  open: boolean
  onClose: () => void
  bundlesFhirComAssinaturaPendente: BundleFhirComAssinaturaPendente[]
  atendimentoId: ID
  reagendarLembrete: (data: Instant) => Promise<LembreteProfissional | undefined>
}

export interface BundleFhirAssinatura extends BundleFhirComAssinaturaPendente {
  assinado?: boolean
}

export function AssinadorRepmModal(props: Readonly<AssinadorRepmModalProps>) {
  const { open, onClose, bundlesFhirComAssinaturaPendente = [], atendimentoId, reagendarLembrete } = props

  const [bundlesFhirAssinatura, setBundlesFhirAssinatura] = useState<BundleFhirAssinatura[]>(
    bundlesFhirComAssinaturaPendente
  )
  const theme = useTheme()
  const styles = createStyles(theme)
  const { getServerTimeNow } = useServerTime()

  const [ocultarTemporariamente, setOcultarTemporariamente] = useState<boolean>(false)
  const [esconderModal, setEsconderModal] = useState<boolean>(false)

  const mostrarModal = open && !esconderModal

  const handleClose = () => {
    if (ocultarTemporariamente) {
      const dataExibirNovamente = startOfDay(addDays(getServerTimeNow(), 1)).getTime()

      reagendarLembrete(dataExibirNovamente)
    }

    onClose()
  }

  const handleConcluir = async () => {
    const hasAssinaturaPendente = bundlesFhirAssinatura.some((bundle) => !bundle.assinado)

    if (hasAssinaturaPendente) {
      setEsconderModal(true)

      await confirm({
        title: 'Deseja concluir com assinaturas pendentes?',
        render: () => 'Há documentos com assinaturas pendentes. Deseja concluir o atendimento?',
        icon: {
          name: 'infoCircleOutline',
          fill: 'normal',
        },
        confirmLabel: 'Concluir',
        cancelLabel: 'Cancelar',
        onConfirm: () => handleClose(),
        onClose: () => setEsconderModal(false),
      })()
    } else {
      handleClose()
    }
  }

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setOcultarTemporariamente(event.target.checked)
  }

  return (
    <Modal open={mostrarModal} onClose={handleClose} size='auto' closeOnBackdropClick={false}>
      <ModalHeader
        title='Assinar documentos RNDS'
        subtitle='É necessário assinar digitalmente os documentos gerados neste atendimento para envio à RNDS.'
        icon={{ name: 'infoCircleOutline', fill: 'normal' }}
      />
      <AssinadorRepmModalBody
        bundlesFhirAssinatura={bundlesFhirAssinatura}
        setBundlesFhirAssinatura={setBundlesFhirAssinatura}
        atendimentoId={atendimentoId}
      />
      <ModalFooter>
        <HFlow justifyContent='flex-end'>
          <Checkbox
            label='Ocultar temporariamente este aviso'
            onChange={handleCheckboxChange}
            style={styles.checkbox}
            checked={ocultarTemporariamente}
          />
          <ModalFooterButton kind='primary' onClick={handleConcluir}>
            Concluir
          </ModalFooterButton>
        </HFlow>
      </ModalFooter>
    </Modal>
  )
}

const createStyles = (theme: Theme) => {
  return {
    checkbox: css`
      font-weight: normal;
      padding: 0.75rem 1rem;
      border-radius: 2px;
      border: 1px solid ${theme.pallete.gray.c40};
      background-color: white;
    `,
  }
}
